import React from 'react';

import { SERVER } from './railLayout';
import {
  BELT,
  DEFECT,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  VERDICT_PASS,
} from './railPalette';

const SLAT_STEP = 36;
const ROLLER_STEP = 70;
const LEG_STEP = 260;

function range(from: number, to: number, step: number): number[] {
  const values: number[] = [];
  for (let value = from; value < to; value += step) values.push(value);
  return values;
}

/**
 * ### ConveyorBelt
 *
 * The rail as line art: paired legs, the slatted top surface, and the front
 * face with its rollers, running out of both sides of the screen. The top
 * surface is the topmost line, so a box standing on it reads as standing on
 * the rail rather than under a rail above it. Nothing
 * here is filled except with the background, so the belt reads as drawn
 * rather than painted. Drawn once and never animated; boxes move over it.
 */
export function ConveyorBelt() {
  const { start, end, top, frontEdge, bottom, skew } = BELT;
  const rollerY = (frontEdge + bottom) / 2;
  const topSurface = `${start + skew},${top} ${end + skew},${top} ${end},${frontEdge} ${start},${frontEdge}`;

  return (
    <g fill="none" stroke={INK} strokeWidth={1.5}>
      {/* Both legs stand on one floor line; a back leg ending higher reads as broken */}
      {range(start + 60, end, LEG_STEP).map((x) => (
        <g key={`legs-${x}`} fill={PAPER}>
          <rect
            x={x + 30}
            y={bottom - 4}
            width={14}
            height={78}
            stroke={INK_SOFT}
          />
          <rect x={x} y={bottom} width={16} height={74} />
          <line
            x1={x - 6}
            y1={bottom + 74}
            x2={x + 50}
            y2={bottom + 74}
            strokeWidth={2}
          />
        </g>
      ))}

      <clipPath id="prl-belt-top">
        <polygon points={topSurface} />
      </clipPath>
      <polygon points={topSurface} fill={PAPER} strokeWidth={2} />
      <g clipPath="url(#prl-belt-top)" stroke={INK_FAINT}>
        {range(start, end + skew, SLAT_STEP).map((x) => (
          <line
            key={`slat-${x}`}
            x1={x + skew}
            y1={top}
            x2={x}
            y2={frontEdge}
          />
        ))}
      </g>

      <rect
        x={start}
        y={frontEdge}
        width={end - start}
        height={bottom - frontEdge}
        fill={PAPER}
        strokeWidth={2}
      />
      {range(start + 30, end, ROLLER_STEP).map((x) => (
        <circle
          key={`roller-${x}`}
          cx={x}
          cy={rollerY}
          r={5}
          stroke={INK_SOFT}
        />
      ))}
    </g>
  );
}

const LED_OFFSETS = [30, 54, 78] as const;

/**
 * ### AiServer
 *
 * The analysis tower at the right of the line, standing on the floor the
 * belt legs reach: front face with drive slots, a vent grid, LEDs that blink
 * in turn, and a verdict badge that reads OK on green until a defect's sound
 * arrives and NG on red while it does; a lit top and a shaded side. The belt
 * runs on behind it, so boxes pass behind it on their way out.
 */
export function AiServer() {
  const { x, y, width, height, depth } = SERVER;
  const rise = depth * 0.7;

  return (
    <g fill={PAPER} stroke={INK} strokeWidth={1.5}>
      <polygon
        points={`${x},${y} ${x + width},${y} ${x + width + depth},${y - rise} ${x + depth},${y - rise}`}
      />
      <polygon
        points={`${x + width},${y} ${x + width + depth},${y - rise} ${x + width + depth},${y + height - rise} ${x + width},${y + height}`}
      />
      <rect x={x} y={y} width={width} height={height} />
      <g stroke={INK_SOFT} strokeLinecap="round">
        {range(y + 30, y + 200, 22).map((slotY) => (
          <line
            key={slotY}
            x1={x + 24}
            y1={slotY}
            x2={x + width - 12}
            y2={slotY}
            strokeWidth={2.5}
          />
        ))}
      </g>
      <g stroke={INK_FAINT}>
        {range(x + 14, x + width - 10, 8).map((ventX) =>
          range(y + 222, y + 262, 8).map((ventY) => (
            <circle
              key={`${ventX}-${ventY}`}
              cx={ventX}
              cy={ventY}
              r={1.4}
              fill={INK_FAINT}
              stroke="none"
            />
          )),
        )}
      </g>
      {LED_OFFSETS.map((offset, index) => (
        <circle
          key={offset}
          className="prl-led"
          cx={x + 15}
          cy={y + offset}
          r={3}
          fill={INK}
          stroke="none"
          style={{ animationDelay: `${index * 0.4}s` }}
        />
      ))}
      {/*
       * The verdict is a filled badge, not text on the case: at this size a
       * coloured word is lost, while a block of colour reads across the room.
       * Both are drawn in the same place and only one is ever shown.
       */}
      <g className="prl-verdict-ok">
        <rect
          x={x + 14}
          y={y + 268}
          width={width - 28}
          height={32}
          rx={4}
          fill={VERDICT_PASS}
          stroke="none"
        />
        <text
          x={x + width / 2}
          y={y + 290}
          textAnchor="middle"
          fontSize={17}
          fontWeight={700}
          fill={PAPER}
          stroke="none"
        >
          OK
        </text>
      </g>
      <g className="prl-verdict-ng" opacity={0}>
        <rect
          x={x + 14}
          y={y + 268}
          width={width - 28}
          height={32}
          rx={4}
          fill={DEFECT}
          stroke="none"
        />
        <text
          x={x + width / 2}
          y={y + 290}
          textAnchor="middle"
          fontSize={17}
          fontWeight={700}
          fill="#ffffff"
          stroke="none"
        >
          NG
        </text>
      </g>
      <circle
        className="prl-led-alert"
        cx={x + width - 12}
        cy={y + 24}
        r={3.5}
        fill={DEFECT}
        stroke="none"
        opacity={0}
      />
    </g>
  );
}
